// src/hooks/useOrchestration.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, onSnapshot, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

/**
 * Manages contest orchestration: active act tracking, auto-advance, panic controls.
 * Reads/writes orchestration fields on the contest doc.
 */
export const useOrchestration = (contestId) => {
  const [contest, setContest] = useState(null);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [countdown, setCountdown] = useState(null);
  const intervalRef = useRef(null);

  // Subscribe to contest doc for orchestration state
  useEffect(() => {
    if (!contestId) return;
    const unsub = onSnapshot(doc(db, 'contests', contestId), (snap) => {
      if (snap.exists()) setContest({ id: snap.id, ...snap.data() });
    });
    return unsub;
  }, [contestId]);

  // Auto-advance timer: check every 5 seconds
  useEffect(() => {
    if (!contest || contest.orchestrationMode !== 'auto' || !contest.autoAdvanceAt) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    const check = async () => {
      const now = Date.now();
      const advanceAt = contest.autoAdvanceAt?.toDate?.()
        ? contest.autoAdvanceAt.toDate().getTime()
        : new Date(contest.autoAdvanceAt).getTime();

      if (now >= advanceAt) {
        const order = contest.runningOrder || [];
        const currentIdx = order.findIndex(a => a.contestantId === contest.activeContestantId);
        if (currentIdx < order.length - 1) {
          const next = order[currentIdx + 1];
          await updateDoc(doc(db, 'contests', contestId), {
            activeContestantId: next.contestantId,
            activeOrder: next.order,
            autoAdvanceAt: Timestamp.fromDate(new Date(now + (next.durationSeconds || 180) * 1000)),
          });
        }
        // If last act — stay on it, don't clear. Voting stays open.
      }
    };

    intervalRef.current = setInterval(check, 5000);
    return () => clearInterval(intervalRef.current);
  }, [contest, contestId]);

  // Update elapsed time and countdown every second
  useEffect(() => {
    const tick = setInterval(() => {
      if (!contest?.activeContestantId || !contest?.runningOrder) return;

      const order = contest.runningOrder;
      const current = order.find(a => a.contestantId === contest.activeContestantId);
      if (!current?.estimatedStartTime) return;

      const startTime = new Date(current.estimatedStartTime).getTime();
      setTimeElapsed(Math.max(0, Math.floor((Date.now() - startTime) / 1000)));

      if (contest.autoAdvanceAt) {
        const advanceAt = contest.autoAdvanceAt?.toDate?.()
          ? contest.autoAdvanceAt.toDate().getTime()
          : new Date(contest.autoAdvanceAt).getTime();
        setCountdown(Math.max(0, Math.ceil((advanceAt - Date.now()) / 1000)));
      } else {
        setCountdown(null);
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [contest]);

  const contestRef = contestId ? doc(db, 'contests', contestId) : null;

  const goToAct = useCallback(async (direction) => {
    if (!contest?.runningOrder || !contestRef) return;
    const order = contest.runningOrder;
    const currentIdx = order.findIndex(a => a.contestantId === contest.activeContestantId);
    const nextIdx = direction === 'next' ? currentIdx + 1 : currentIdx - 1;
    if (nextIdx < 0 || nextIdx >= order.length) return;

    const next = order[nextIdx];
    const update = {
      activeContestantId: next.contestantId,
      activeOrder: next.order,
    };
    if (contest.orchestrationMode === 'auto') {
      update.autoAdvanceAt = Timestamp.fromDate(new Date(Date.now() + (next.durationSeconds || 180) * 1000));
    }
    await updateDoc(contestRef, update);
  }, [contest, contestRef]);

  const nextAct = useCallback(() => goToAct('next'), [goToAct]);
  const prevAct = useCallback(() => goToAct('prev'), [goToAct]);

  const pauseAuto = useCallback(async () => {
    if (!contestRef) return;
    await updateDoc(contestRef, { orchestrationMode: 'manual', autoAdvanceAt: null });
  }, [contestRef]);

  const resumeAuto = useCallback(async () => {
    if (!contest?.runningOrder || !contestRef) return;
    const current = contest.runningOrder.find(a => a.contestantId === contest.activeContestantId);
    const duration = current?.durationSeconds || 180;
    await updateDoc(contestRef, {
      orchestrationMode: 'auto',
      autoAdvanceAt: Timestamp.fromDate(new Date(Date.now() + duration * 1000)),
    });
  }, [contest, contestRef]);

  const startOrchestration = useCallback(async () => {
    if (!contest?.runningOrder?.length || !contestRef) return;
    const first = contest.runningOrder[0];
    await updateDoc(contestRef, {
      activeContestantId: first.contestantId,
      activeOrder: first.order,
      orchestrationMode: 'auto',
      autoAdvanceAt: Timestamp.fromDate(new Date(Date.now() + (first.durationSeconds || 180) * 1000)),
      closed: false,
    });
  }, [contest, contestRef]);

  const closeContest = useCallback(async () => {
    if (!contestRef) return;
    await updateDoc(contestRef, {
      closed: true,
      orchestrationMode: 'manual',
      autoAdvanceAt: null,
    });
  }, [contestRef]);

  // Derived state
  const currentAct = contest?.runningOrder?.find(a => a.contestantId === contest?.activeContestantId) || null;
  const totalActs = contest?.runningOrder?.length || 0;
  const isOrchestrating = !!contest?.activeContestantId;

  return {
    contest,
    currentAct,
    totalActs,
    isOrchestrating,
    timeElapsed,
    countdown,
    nextAct,
    prevAct,
    pauseAuto,
    resumeAuto,
    startOrchestration,
    closeContest,
  };
};
